<template>
    <div v-bind:class="{'code-input-container': true, [className]: !!className}">
        <div class="code-input">
            <template v-for="(v, index) in values">
                <div :style="`max-width:${fieldWidth}px; max-height:${fieldHeight}px; margin:0 0.${$vuetify.breakpoint.xsOnly ? '1' : '2'}em;`"
                    :key="`code-input-${index}`" data-cy="code-input">
                    <input :type="type === 'number' ? 'tel' : type"
                        :pattern="type === 'number' ? '[0-9]' : null"
                        :autoFocus="autoFoc && !loading && index === autoFocusIndex"
                        :class="{'input-error' : isError}"
                        style="height:100%; width:100%; padding:1em 0.5em;"
                        :data-id="index" :value="v" :ref="iRefs[index]"
                        @input="onValueChange" @focus="onFocus" @keydown="onKeyDown"
                        :disabled="disabled" :required="required" maxlength="1" />
                </div>
            </template>
        </div>
        <v-fade-transition>
            <div v-if="isError"
                :style="`position:absolute; top:${fieldHeight + fieldHeight*0.05}px; width:100%;`">
                <div class="pa-3 subtitle grey--text text--darken-2 d-flex flex-column align-center justify-center"
                    style="text-align:center; line-height:1em;">
                    <span>
                        {{ error }}
                    </span>
                </div>
            </div>
        </v-fade-transition>
    </div>
</template>

<script>
// Fork of: https://www.npmjs.com/package/vue-verification-code-input
export default {
    name: 'CodeInput',
    props: {
        type: {
            type: String,
            default: 'number'
        },
        className: {
            type: String
        },
        fieldCount: {
            type: Number,
            default: 6
        },
        fieldWidth: {
            type: Number,
            default: 58
        },
        fieldHeight: {
            type: Number,
            default: 54
        },
        autoFoc: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        change: {
            type: Function
        },
        complete: {
            type: Function
        },
        loading: {
            type: Boolean,
            default: false
        },
        error: {
            type: String
        }
    },
    data() {
        const { fieldCount, values } = this
        let vals
        let autoFocusIndex = 0

        if (values && values.length) {
            vals = []
            for (let i = 0; i < fieldCount; i++) {
                vals.push(values[i] || '')
            }
            autoFocusIndex = values.length >= fieldCount ? 0 : values.length
        } else {
            vals = Array(fieldCount).fill('')
        }

        this.iRefs = []
        for (let i = 0; i < fieldCount; i++) {
            this.iRefs.push(`input_${i}`)
        }

        return {
            values: vals,
            autoFocusIndex,
            isError: false
        }
    },
    methods: {
        onFocus(e) {
            e.target.select(e)
        },
        onValueChange(e) {
            this.isError = false
            const index = parseInt(e.target.dataset.id)
            const { type, fieldCount } = this
            if (type === 'number') {
                e.target.value = e.target.value.replace(/[^\d]/gi, '')
            }
            if (
                e.target.value === '' ||
                (type === 'number' && !e.target.validity.valid)
            ) {
                return
            }
            let next
            const value = e.target.value
            let { values } = this
            values = Object.assign([], values)
            if (value.length > 1) {
                let nextIndex = value.length + index - 1
                if (nextIndex >= fieldCount) {
                    nextIndex = fieldCount - 1
                }
                next = this.iRefs[nextIndex]
                const split = value.split('')
                split.forEach((item, i) => {
                    const cursor = index + i
                    if (cursor < fieldCount) {
                        values[cursor] = item
                    }
                })
                this.values = values
            } else {
                next = this.iRefs[index + 1]
                values[index] = value
                this.values = values
            }
            if (next) {
                const element = this.$refs[next][0]
                element.focus()
                element.select()
            }
            this.triggerChange(values)
        },
        onKeyDown(e) {
            this.isError = false
            // Get the current, prev, and next index
            const index = parseInt(e.target.dataset.id)
            const prevIndex = index - 1
            const nextIndex = index + 1

            // Get the element reference of the prev and next inputs
            const prev = this.iRefs[prevIndex]
            const next = this.iRefs[nextIndex]

            //
            switch (e.key.toLowerCase()) {
                case 'backspace': {
                    e.preventDefault()
                    const vals = [...this.values]
                    // If there is a character on the focused input, delete it
                    if (this.values[index]) {
                        vals[index] = ''
                        this.values = vals
                        this.triggerChange(vals)
                    }
                    // Otherwise delete the previous character, and move focus back
                    else if (prev) {
                        vals[prevIndex] = ''
                        this.$refs[prev][0].focus()
                        this.values = vals
                        this.triggerChange(vals)
                    }
                    break
                }
                // Move focus back
                case 'ArrowLeft':
                    e.preventDefault()
                    if (prev) {
                        this.$refs[prev][0].focus()
                    }
                    break
                // Move focus forward
                case 'ArrowRight':
                    e.preventDefault()
                    if (next) {
                        this.$refs[next][0].focus()
                    }
                    break
                case 'ArrowUp':
                case 'ArrowDown':
                    e.preventDefault()
                    break
                default:
                    break
            }
        },
        triggerChange(values = this.values) {
            // Get the number of fields
            const { fieldCount } = this

            // Get the current input
            const val = values.join('')

            // Emit the change event
            this.$emit('change', val)

            // If the value length is greater or equal than the amount of fileds, emit the complete event
            if (val.length >= fieldCount) {
                this.$emit('complete', val)
            }
        },
        resetValues() {
            // Clear the values
            for (let i = 0; i < this.values.length; ++i) {
                this.values[i] = ''
            }

            // reset values so that vue sees changes
            this.values = [...this.values]

            // Shift focus to first element
            this.$refs[this.iRefs[0]][0].focus()
        }
    },
    watch: {
        error(err) {
            if (!!err) {
                this.isError = true
            }
        }
    }
}
</script>

<style scoped>
.code-input-container {
    position: relative;
}
.code-input {
    display: flex;
    flex-direction: row;
}
.code-input > div > input {
    transition: border 0.1s ease-out;
    border: solid 1px #a8adb7;
    border-right: none;
    font-size: 20px;
    color: #525461;
    text-align: center;
    box-sizing: border-box;
    border-radius: 0;
    -webkit-appearance: initial;
}
.code-input > div > input:last-child {
    border-right: solid 1px #a8adb7;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;
}
.code-input > div > input:first-child {
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
}
.code-input > div > input:focus {
    outline: none;
    border: 1px solid var(--md-purple);
    caret-color: var(--md-purple);
}
.code-input > div > input:focus + input {
    border-left: none;
}
.blur {
    position: absolute;
    inset: 0;
    background-color: #fff;
    opacity: 0.5;
    filter: blur(0.5px);
    transition: opacity 0.3s;
}
.input-error {
    border-color: #c95e5e !important;
}
</style>